import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PublicDataTable from '../components/PublicDataTable';
import PublicFilterDropdown from '../components/PublicFilterDropdown';
import { CheckboxFilter } from '../components/FilterPanel';
import ContactCell from '../components/ContactCell';
import StatusBadge from '../components/ProfessionalStatusBadge';
import FacilityMapView from '../components/Facilities/FacilityMapView';
import { DirectoryHeader, DirectoryPage, FilterBy, LastUpdated, UnderlineTabs } from '../components/Directory';
import {
  CAHWS,
  PROFESSIONAL_STATES,
  PROFESSIONALS_LAST_UPDATED,
  TECHNICAL_PROFESSIONALS,
  VETERINARIANS,
} from '../data/professionalsData';

const statusColumn = { key: 'status', title: 'Professional Status', render: (v) => <StatusBadge status={v || '—'} /> };
const contactColumn = { key: 'contact', title: 'Contact', divider: true, render: (v) => <ContactCell number={v} /> };

// Figma 1142:28586 — Figma draws a vertical rule after Contact. The Action
// column ("View") is appended in the component, where it can open the map.
const VET_COLUMNS = [
  { key: 'name',           title: 'Veterinarian Name' },
  contactColumn,
  { key: 'practice',       title: 'Practice / Facility' },
  { key: 'specialisation', title: 'Specialisation' },
  { key: 'services',       title: 'Services Provided' },
  { key: 'licence',        title: 'Licence / Registration No.' },
  statusColumn,
];

// CAHWs frame (1143:29063) couldn't be read column-by-column — these mirror
// the Veterinarians table for community workers; adjust once confirmed
const CAHW_COLUMNS = [
  { key: 'name',       title: 'CAHW Name' },
  contactColumn,
  { key: 'lga',        title: 'LGA' },
  { key: 'community',  title: 'Community' },
  { key: 'services',   title: 'Services Provided' },
  { key: 'supervisor', title: 'Supervising Veterinarian' },
  statusColumn,
];

// Mirrors the Veterinarians table until the Figma frame's columns are confirmed
const TECHNICAL_COLUMNS = [
  { key: 'name',           title: 'Professional Name' },
  contactColumn,
  { key: 'profession',     title: 'Profession' },
  { key: 'specialisation', title: 'Specialisation' },
  { key: 'services',       title: 'Services Provided' },
  { key: 'licence',        title: 'Licence / Registration No.' },
  statusColumn,
];

// Veterinary Professionals' "Facility Type" filter reads the practice /
// facility each vet works from
const FACILITY_TYPES = [...new Set(VETERINARIANS.map((v) => v.practice))];

// Underline tabs from Figma 1142:28586 / 1295:58606 / 1293:52563
const GROUPS = [
  { key: 'veterinarians', label: 'Veterinary Professionals',              rows: VETERINARIANS,           columns: VET_COLUMNS },
  { key: 'cahws',         label: 'Community & Para-veterinary Workforce', rows: CAHWS,                   columns: CAHW_COLUMNS },
  { key: 'technical',     label: 'Livestock Technical Professionals',     rows: TECHNICAL_PROFESSIONALS, columns: TECHNICAL_COLUMNS },
];

export default function ServiceProfessionals() {
  const location = useLocation();
  const [groupKey, setGroupKey] = useState(
    GROUPS.some((g) => g.key === location.state?.group) ? location.state.group : GROUPS[0].key
  );
  const [state, setState] = useState(PROFESSIONAL_STATES[0]);
  const [query, setQuery] = useState('');
  // Multi-select Facility Type — [] means all types
  const [facilityTypes, setFacilityTypes] = useState([]);
  // Veterinarian whose "View" was clicked — shows the map view in place of the table
  const [mapRecord, setMapRecord] = useState(null);

  const group = GROUPS.find((g) => g.key === groupKey);

  const columns = useMemo(() => (groupKey !== 'veterinarians' ? group.columns : [
    ...group.columns,
    {
      key: '__action',
      title: 'Action',
      render: (_, row) => (
        <button
          type="button"
          onClick={() => setMapRecord(row)}
          className="text-[11px] font-medium text-[#28ba12] underline underline-offset-2 hover:no-underline"
        >
          View
        </button>
      ),
    },
  ]), [group, groupKey]);

  function handleGroupChange(key) {
    setGroupKey(key);
    setFacilityTypes([]);
  }

  const rows = useMemo(() => group.rows.filter((r) =>
    (state === 'All States' || r.state === state)
    && (!facilityTypes.length || facilityTypes.includes(r.practice))
  ), [group, state, facilityTypes]);

  // Figma 1293:53438 — the map view replaces the heading, search and tabs
  if (mapRecord) {
    return (
      <DirectoryPage>
        <FacilityMapView
          record={mapRecord}
          onBack={() => setMapRecord(null)}
          badge={<StatusBadge status={mapRecord.status} />}
          label={group.label}
          lastUpdated={PROFESSIONALS_LAST_UPDATED}
        />
      </DirectoryPage>
    );
  }

  return (
    <DirectoryPage>
      <DirectoryHeader
        eyebrow="Service Professionals Directory"
        subject="Livestock Service Professionals"
        query={query}
        onQueryChange={setQuery}
      />

      <div className="flex flex-col gap-4">
        <UnderlineTabs
          tabs={GROUPS}
          active={groupKey}
          onChange={handleGroupChange}
          right={(
            <FilterBy>
              <PublicFilterDropdown
                value={state}
                options={PROFESSIONAL_STATES}
                onChange={setState}
                ariaLabel="Filter by state"
                align="right"
              />
              {groupKey === 'veterinarians' && (
                <CheckboxFilter
                  label="Facility Type"
                  options={FACILITY_TYPES}
                  selected={facilityTypes}
                  onChange={setFacilityTypes}
                  align="right"
                />
              )}
            </FilterBy>
          )}
        />

        <PublicDataTable key={groupKey} columns={columns} data={rows} searchQuery={query} pageSize={10} />
      </div>

      <LastUpdated date={PROFESSIONALS_LAST_UPDATED} />
    </DirectoryPage>
  );
}
